import React, { useEffect, useMemo, useRef, useState } from "react";
import { getDynamicLabel, getDynamicValue } from "@/components/add-v2/validate";

function getVisibility(ctx, visibilityRules) {
  return (
    (visibilityRules || [])?.reduce((acc, curr) => {
      if (curr.equal)
        return acc && getDynamicValue(ctx, curr.path) + "" === curr?.equal + "";
      return acc && getDynamicValue(ctx, curr.path);
    }, true) || false
  );
}
export function FormController({ Component, field, ctxRef, ...props }) {
  const { label, dependencies, composite, visibilityRules } = field;
  const initialVisible = useMemo(() => {
    return getVisibility(ctxRef.current, visibilityRules);
  }, []);
  const [visible, setVisible] = useState(initialVisible);
  const lastVisible = useRef(initialVisible);
  const [labelObject, setLableObject] = useState(
    label?.dynamic ? getDynamicLabel({ label, ctx: ctxRef.current }) : label,
  );
  const [priceBlurred, setPriceBlurred] = useState(false);

  useEffect(() => {
    setLableObject(
      label?.dynamic ? getDynamicLabel({ label, ctx: ctxRef.current }) : label,
    );
  }, [label]);

  useEffect(() => {
    let revalidateMap =
      field.visibilityRules?.reduce(
        (acc, curr) => ({ ...acc, [curr.path]: true }),
        {},
      ) || {};

    revalidateMap = {
      ...revalidateMap,
      ...dependencies?.reduce((acc, curr) => ({ ...acc, [curr]: true }), {}),
    };
    const ctx = ctxRef.current || {};

    const currentVisible = getVisibility(ctx, visibilityRules);
    setVisible(currentVisible);
    lastVisible.current = currentVisible;

    function handleCtxChange(e) {
      const { ctx: c = {}, name, value, nested } = e.detail || {};

      const ctx = ctxRef.current || {};

      const nestedName = [nested, name].filter(Boolean).join(".");

      if (!revalidateMap[nestedName || name]) return;
      if (nested) {
        ctx[nested] = {
          ...(ctx[nested] || {}),
          [name]: value,
        };
      } else {
        ctx[name] = value;
      }
      setLableObject(label?.dynamic ? getDynamicLabel({ label, ctx }) : label);
      const currentVisible = getVisibility(ctx, visibilityRules);

      if (currentVisible === lastVisible.current) return;
      lastVisible.current = currentVisible;
      setVisible(currentVisible);

      if (composite) {
        for (const c of composite) {
          ctxRef.current[c] = field?.defaultValue;
        }
      }
      document.dispatchEvent(
        new CustomEvent("visibilitychange", {
          bubbles: true,
          detail: {
            visible: currentVisible,
            name: field.name,
            value: field?.defaultValue,
            nested,
          },
        }),
      );
    }

    function handlePriceBlur(e) {
      const { name, value } = e.target || {};

      if (name === "price" && value) {
        setPriceBlurred(true);

        if (field.name === "premiums") {
          const ctx = ctxRef.current || {};
          const currentVisible = getVisibility(ctx, visibilityRules);

          if (currentVisible === lastVisible.current) return;
          lastVisible.current = currentVisible;
          setVisible(currentVisible);

          document.dispatchEvent(
            new CustomEvent("visibilitychange", {
              bubbles: true,
              detail: {
                visible: currentVisible,
                name: field.name,
                value: field?.defaultValue,
              },
            }),
          );
        }
      }
    }

    document.addEventListener("ctxchange", handleCtxChange);
    document.addEventListener("focusout", handlePriceBlur);
    return () => {
      document.removeEventListener("ctxchange", handleCtxChange);
      document.removeEventListener("focusout", handlePriceBlur);
    };
  }, [composite]);

  if (field.name === "premiums") {
    const hasPremiumPrice = ctxRef.current?.price_12_payments || ctxRef.current?.price_4_payments || ctxRef.current?.price_2_payments;
    if (hasPremiumPrice || (priceBlurred && ctxRef.current?.price)) {
      return (
        <Component
          {...field}
          label={labelObject}
          key={field.name}
          ctxRef={ctxRef}
          {...props}
        />
      );
    }
    return null;
  }

  if (!visible) return null;

  return (
    <Component
      {...field}
      label={labelObject}
      key={field.name}
      ctxRef={ctxRef}
      {...props}
    />
  );
}
